import React, { useState } from 'react';
import { Modal, Form, Select, Upload, Button, Progress, Table, Tag, Space, App } from 'antd';
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import { importCustomer } from '@/services/ant-design-pro/api';
import type { CustomerImportDTO } from '@/services/ant-design-pro/typings';
import { CUSTOMER_TYPE_OPTIONS } from '@/constants/customer';
import { downloadFile, buildDownloadUrl } from '@/utils/download';

const { Dragger } = Upload;
const { Option } = Select;

interface CustomerImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const CustomerImportModal: React.FC<CustomerImportModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<{
    numberFail: number;
    numberSuccesses: number;
    succeedList: CustomerImportDTO[];
    storageVO?: any;
  } | null>(null);
  const [step, setStep] = useState<'upload' | 'result'>('upload');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 重置状态
  const resetState = () => {
    setFileList([]);
    setImportResult(null);
    setStep('upload');
    form.resetFields();
    setPagination({ current: 1, pageSize: 10 });
  };

  // 处理取消
  const handleCancel = () => {
    resetState();
    onCancel();
  };

  // 处理文件上传
  const handleUpload = {
    beforeUpload: (file: File) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传Excel文件!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB!');
        return false;
      }
      setFileList([file]);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // 处理导入
  const handleImport = async () => {
    try {
      const values = await form.validateFields();
      if (fileList.length === 0) {
        message.error('请选择要导入的文件!');
        return;
      }

      setLoading(true);
      const response = await importCustomer(fileList[0], values.customerType);

      if (response.success && response.result) {
        setImportResult({
          numberFail: response.result.numberFail || 0,
          numberSuccesses: response.result.numberSuccesses || 0,
          succeedList: response.result.succeedList || [],
          storageVO: response.result.storageVO,
        });
        setStep('result');

        const { numberSuccesses, numberFail } = response.result;
        if (numberSuccesses > 0 && numberFail === 0) {
          message.success(`导入完成! 成功导入 ${numberSuccesses} 条客户信息`);
        } else if (numberSuccesses > 0 && numberFail > 0) {
          message.warning(`导入完成! 成功导入 ${numberSuccesses} 条，失败 ${numberFail} 条`);
        } else {
          message.error(`导入失败! 共 ${numberFail} 条记录导入失败`);
        }
      } else {
        message.error(response.message || '导入失败');
      }
    } catch (error) {
      console.error('导入客户失败:', error);
      message.error('导入失败');
    } finally {
      setLoading(false);
    }
  };

  // 下载失败文件
  const handleDownloadFailed = () => {
    if (!importResult?.storageVO?.id) {
      message.error('失败文件不存在');
      return;
    }

    try {
      // 构建下载URL
      const downloadUrl = buildDownloadUrl(importResult.storageVO.id);

      // 使用通用下载方法
      downloadFile(downloadUrl, '导入失败客户列表.xlsx');

      message.success('失败文件下载成功');
    } catch (error) {
      console.error('下载失败文件失败:', error);
      message.error('下载失败文件失败，请稍后重试');
    }
  };

  // 结果表格列定义
  const resultColumns = [
    {
      title: '客户名称',
      dataIndex: 'customerName',
      key: 'customerName',
      ellipsis: true,
    },
    {
      title: '客户类型',
      dataIndex: 'customerTypeName',
      key: 'customerTypeName',
      width: 120,
    },
    {
      title: '证件号码',
      dataIndex: 'idNumber',
      key: 'idNumber',
      ellipsis: true,
      render: (_: any, record: CustomerImportDTO) => (
        <>
          {record.idNumber || record.registrationCertificateNumber}
        </>
      ),
    },
    {
      title: '联系人',
      dataIndex: 'customerContactName',
      key: 'customerContactName',
    },
    {
      title: '联系方式',
      dataIndex: 'customerContactMethod',
      key: 'customerContactMethod',
    }
  ];

  return (
    <Modal
      title="导入客户信息"
      open={visible}
      onCancel={handleCancel}
      width={800}
      maskClosable={false}
      footer={
        step === 'upload' ? (
          <Space>
            <Button onClick={handleCancel}>取消</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleImport}
              disabled={fileList.length === 0}
            >
              开始导入
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleCancel}>关闭</Button>
            {importResult?.storageVO && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadFailed}
              >
                下载失败文件
              </Button>
            )}
            <Button type="primary" onClick={() => {
              resetState();
              onSuccess();
            }}>
              完成
            </Button>
          </Space>
        )
      }
    >
      {step === 'upload' ? (
        <div>
          <Form form={form} layout="vertical">
            <Form.Item
              name="customerType"
              label="客户类型"
              rules={[{ required: true, message: '请选择客户类型' }]}
            >
              <Select placeholder="请选择客户类型">
                {CUSTOMER_TYPE_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>

          <div style={{ marginTop: 16 }}>
            <Dragger
              {...handleUpload}
              fileList={fileList}
              accept=".xlsx,.xls"
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持单个Excel文件上传，文件大小不超过10MB
              </p>
            </Dragger>
          </div>

          {loading && (
            <div style={{ marginTop: 16 }}>
              <Progress percent={50} status="active" />
              <p style={{ textAlign: 'center', marginTop: 8 }}>
                正在导入客户信息，请稍候...
              </p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h4>导入结果</h4>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <Tag color="success">
                成功: {importResult?.numberSuccesses || 0} 条
              </Tag>
              <Tag color="error">
                失败: {importResult?.numberFail || 0} 条
              </Tag>
              <Tag color="processing">
                总计: {(importResult?.numberSuccesses || 0) + (importResult?.numberFail || 0)} 条
              </Tag>
            </div>
          </div>

          <Table
            columns={resultColumns}
            dataSource={importResult?.succeedList || []}
            rowKey={(record, index) => `${record.customerName}-${index}`}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: importResult?.succeedList?.length || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination({
                  current: page,
                  pageSize: pageSize || pagination.pageSize,
                });
              },
              onShowSizeChange: (current, size) => {
                setPagination({
                  current: 1,
                  pageSize: size,
                });
              },
            }}
            scroll={{ x: 800 }}
            size="small"
          />
        </div>
      )}
    </Modal>
  );
};

export default CustomerImportModal;
